/**
 * "Frequently used" emoji (Slack; logic/frequentEmoji): my list from the server, bumped at once when I use one. A
 * composer pick is also reported to the server; a reaction isn't (adding a reaction counts there by itself). The
 * pickers show the list first and the hover bar's quick reactions are its top three.
 *
 * Also my skin tone (Slack's picker preference, per person and workspace on the server): the pickers draw emoji in it,
 * and the frequent list, the quick reactions and `:` autocomplete picks are shown / inserted in it (logic/emoji
 * `withSkinTone`).
 */
import type { SkinTone } from "@bundle/contracts";
import { withSkinTone, withoutSkinTone } from "../logic/emoji.ts";
import { DEFAULT_FREQUENT, bumpFrequent, quickReactions } from "../logic/frequentEmoji.ts";
import { ViewModel } from "./ViewModel.ts";
import type { WorkspaceViewModel } from "./WorkspaceViewModel.ts";

export type EmojiUsageState = { frequent: string[]; quick: string[]; skinTone: SkinTone };

function toned(list: readonly string[], tone: SkinTone): string[] {
  return [...new Set(list.map((e) => withSkinTone(e, tone)))];
}

export class EmojiUsageViewModel extends ViewModel<EmojiUsageState> {
  /** The list as used (before my skin tone is applied). */
  private raw: string[] = [...DEFAULT_FREQUENT];

  constructor(private readonly ws: WorkspaceViewModel) {
    super({ frequent: [...DEFAULT_FREQUENT], quick: quickReactions([]), skinTone: "none" });
  }

  private show(raw: string[], skinTone: SkinTone): void {
    this.raw = raw;
    const frequent = toned(raw, skinTone);
    this.update({ frequent, quick: quickReactions(frequent), skinTone });
  }

  refresh(): Promise<void> {
    return this.ws.deps.api.frequentEmoji(this.ws.wsId).then(
      (res) => {
        if (this.isDisposed) return;
        this.show(res.emoji.length > 0 ? res.emoji : this.raw, res.skinTone ?? this.state.skinTone);
      },
      () => undefined,
    );
  }

  /**
   * I used `emoji`: first in the list now; `report` (composer picks) tells the server too. The list keeps it without a
   * tone, so it follows my skin tone when I change it.
   */
  use(emoji: string, report: boolean): void {
    this.show(bumpFrequent(this.raw, withoutSkinTone(emoji)), this.state.skinTone);
    if (report) this.ws.deps.api.recordEmoji(this.ws.wsId, [emoji]).catch(() => undefined);
  }

  /** A `:` autocomplete pick: counted like a composer pick, and inserted in my skin tone (returned). */
  pick(emoji: string): string {
    const e = withSkinTone(emoji, this.state.skinTone);
    this.use(e, true);
    return e;
  }

  /** Picks my skin tone (the picker's hand button): shown at once, saved on the server; put back if that fails. */
  setSkinTone(tone: SkinTone): void {
    const before = this.state.skinTone;
    if (tone === before) return;
    this.show(this.raw, tone);
    this.ws.deps.api.setSkinTone(this.ws.wsId, tone).catch(() => {
      if (!this.isDisposed && this.state.skinTone === tone) this.show(this.raw, before);
    });
  }
}
